import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import { Clients } from "@/lib/clients";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../D1StaticData/edit-form-conf";
import { useTranslation } from "react-i18next";

import { Tables } from "@/gen/tableNames/tables";

const filterD1TimeSeriesSchema = z.object({
  d1_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterD1TimeSeriesFields = filterD1TimeSeriesSchema.keyof().enum;

export type FilterD1TimeSeriesSchema = z.infer<typeof filterD1TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterD1TimeSeriesSchema>[] = [
  {
    name: filterD1TimeSeriesFields.d1_static_datum_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterD1TimeSeriesFields.isin_contains,
    key: "isin",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterD1TimeSeries() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.D1_TIME_SERIES;

  const fields: Field[] = [
    {
      name: filterD1TimeSeriesFields.d1_static_datum_name_contains,
      label: t(`${tableName}.searchbox.d1_static_datum_name_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_STATIC_DATA}`,
      id_column: "d1_static_datum_id",
      column_name: "name",
      col_span: 2,
      filters: makeFiltersBuilder<FilterD1TimeSeriesSchema>(
        assetDepFields,
        filterD1TimeSeriesFields.d1_static_datum_name_contains,
      ),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterD1TimeSeriesFields.asset_class_1_equals,
      assetSubclassFieldName: filterD1TimeSeriesFields.asset_class_2_equals,
      assetClassLabel: t(`${Tables.D1_TIME_SERIES}.filter.asset_class_1`),
      assetSubclassLabel: t(`${Tables.D1_TIME_SERIES}.filter.asset_class_2`),
      client: Clients.D1,
      staticDataTableName: Tables.D1_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterD1TimeSeriesFields.isin_contains,
      label: t(`${tableName}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_STATIC_DATA}`,
      id_column: "d1_static_datum_id",
      column_name: "isin",
      filters: makeFiltersBuilder<FilterD1TimeSeriesSchema>(
        assetDepFields,
        filterD1TimeSeriesFields.isin_contains,
      ),
    },
    {
      name: filterD1TimeSeriesFields.currency_contains,
      label: t(`${tableName}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterD1TimeSeriesFields.date_from },
      to: { name: filterD1TimeSeriesFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterD1TimeSeriesFields.create_from },
      to: { name: filterD1TimeSeriesFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      schema={filterD1TimeSeriesSchema}
      client={Clients.D1}
      tableName={Tables.D1_TIME_SERIES}
      fields={fields}
      columns={2}
    />
  );
}
